import { useState, useEffect } from 'react'
import { supabase } from './supabase'

const VS_PAR_LABEL = { '-3': 'Albatross', '-2': 'Eagle', '-1': 'Birdie', '0': 'Par', '1': 'Bogey', '2': 'Double', '3': 'Triple' }
const VS_PAR_COLOR = { '-3': '#C19A6B', '-2': '#C19A6B', '-1': '#00563F', '0': '#1A1814', '1': '#7A7570', '2': '#C0392B', '3': '#C0392B' }

export default function Scorecard({ session, matchId, onBack }) {
  const [match, setMatch] = useState(null)
  const [players, setPlayers] = useState([])
  const [profiles, setProfiles] = useState({})
  const [scores, setScores] = useState({})
  const [myCard, setMyCard] = useState(null)
  const [currentHole, setCurrentHole] = useState(0)
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState(null)
  const [loading, setLoading] = useState(true)
  const [timeLeft, setTimeLeft] = useState(null)

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  useEffect(() => { loadAll() }, [matchId])

  // 24hr review countdown
  useEffect(() => {
    if (!myCard?.review_started_at) { setTimeLeft(null); return }
    const tick = () => {
      const elapsed = Date.now() - new Date(myCard.review_started_at).getTime()
      const remaining = Math.max(0, 24 * 60 * 60 * 1000 - elapsed)
      setTimeLeft(remaining)
      if (remaining === 0) loadAll()
    }
    tick()
    const t = setInterval(tick, 60000)
    return () => clearInterval(t)
  }, [myCard?.review_started_at])

  const loadAll = async () => {
    setLoading(true)
    const { data: m } = await supabase.from('matches').select('*').eq('id', matchId).single()
    setMatch(m)

    const { data: mp } = await supabase.from('match_players').select('*').eq('match_id', matchId)
    const allIds = [session.user.id, ...(mp || []).map(p => p.player_id)]
    setPlayers(allIds)

    const { data: prof } = await supabase.from('profiles').select('id, first_name, last_name').in('id', allIds)
    const pmap = {}
    prof?.forEach(p => { pmap[p.id] = p })
    setProfiles(pmap)

    const { data: sc } = await supabase.from('scorecards').select('*').eq('match_id', matchId)
    const scMap = {}
    sc?.forEach(s => { scMap[s.player_id] = s.hole_scores || [] })
    setScores(scMap)

    const mine = sc?.find(s => s.player_id === session.user.id)
    setMyCard(mine || null)

    if (mine?.hole_scores) {
      const last = mine.hole_scores.reduce((acc, v, i) => v != null ? i : acc, -1)
      if (last >= 0 && last < (m?.num_holes || 18) - 1) setCurrentHole(last + 1)
      else if (last >= 0) setCurrentHole(last)
    }

    setLoading(false)
  }

  const numHoles = match?.num_holes || 18
  const holePars = match?.hole_pars || Array(numHoles).fill(4)
  const myScores = scores[session.user.id] || []
  const currentPar = holePars[currentHole] || 4
  const currentScore = myScores[currentHole] ?? null
  const vsParNum = currentScore !== null ? currentScore - currentPar : null
  const vsParKey = vsParNum !== null ? String(Math.max(-3, Math.min(3, vsParNum))) : null
  const totalStrokes = myScores.reduce((a, b) => b != null ? a + b : a, 0)
  const totalVsPar = myScores.reduce((acc, s, i) => s != null ? acc + (s - (holePars[i] || 4)) : acc, 0)
  const holesPlayed = myScores.filter(s => s != null).length
  const isLive = !myCard || myCard.status === 'live' || myCard.status === 'review'
  const isReview = myCard?.status === 'review'
  const isClosed = myCard?.status === 'closed'

  const upsert = async (newScores, status, extra = {}) => {
    if (myCard) {
      await supabase.from('scorecards').update({ hole_scores: newScores, status, ...extra }).eq('id', myCard.id)
    } else {
      const { data: inserted } = await supabase.from('scorecards').insert({ match_id: matchId, player_id: session.user.id, hole_scores: newScores, status, ...extra }).select().single()
      if (inserted) setMyCard(inserted)
    }
  }

  const updateScore = async (delta) => {
    if (!isLive) return
    const base = currentScore ?? currentPar
    const newVal = Math.max(1, base + delta)
    const newScores = Array(numHoles).fill(null).map((_, i) => myScores[i] ?? null)
    newScores[currentHole] = newVal
    setScores(prev => ({ ...prev, [session.user.id]: newScores }))
    await upsert(newScores, 'live')
  }

  const setHoleScore = async (val) => {
    if (!isLive) return
    const newScores = Array(numHoles).fill(null).map((_, i) => myScores[i] ?? null)
    newScores[currentHole] = Math.max(1, val)
    setScores(prev => ({ ...prev, [session.user.id]: newScores }))
    await upsert(newScores, 'live')
  }

  const saveAndAdvance = async () => {
    if (currentScore === null) return showToast('Enter your score first', 'error')
    setSaving(true)
    const newScores = Array(numHoles).fill(null).map((_, i) => myScores[i] ?? null)
    newScores[currentHole] = currentScore
    const saveStatus = isReview ? 'review' : 'live'
    await upsert(newScores, saveStatus)

    if (isReview) {
      // Reset ALL players' agreed_by in this match — forces re-review
      const { data: allCards } = await supabase.from('scorecards').select('id').eq('match_id', matchId)
      if (allCards) {
        for (const card of allCards) {
          await supabase.from('scorecards').update({ agreed_by: [] }).eq('id', card.id)
        }
      }
    }

    setSaving(false)
    if (!isReview && currentHole < numHoles - 1) setCurrentHole(h => h + 1)
    else if (!isReview) showToast('Last hole saved — submit when ready', 'info')
    else showToast('Correction saved — all players must re-agree', 'success')
  }

  const handleSubmit = async () => {
    if (holesPlayed < numHoles) return showToast('Complete all holes before submitting', 'error')
    if (!window.confirm('Submit your scorecard? It will enter a 24-hour review window.')) return
    setSubmitting(true)
    const reviewStarted = new Date().toISOString()
    await upsert(myScores, 'review', { review_started_at: reviewStarted })
    setSubmitting(false)
    showToast('Scorecard submitted — 24hr review window started', 'success')
    loadAll()
  }

  const formatTimeLeft = (ms) => {
    if (ms === null) return ''
    const h = Math.floor(ms / 3600000)
    const m = Math.floor((ms % 3600000) / 60000)
    return `${h}h ${m}m remaining`
  }

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#7A7570', fontFamily: 'DM Sans, sans-serif' }}>Loading scorecard...</div>
    </div>
  )

  return (
    <div style={s.screen}>
      {toast && <div style={{ ...s.toast, background: toast.type === 'error' ? '#C0392B' : toast.type === 'success' ? '#00563F' : '#1A1814' }}>{toast.msg}</div>}

      {/* Status bar */}
      {isReview && (
        <div style={s.reviewBar}>
          ⏳ REVIEW WINDOW — {formatTimeLeft(timeLeft)} · Corrections allowed
        </div>
      )}
      {isClosed && (
        <div style={s.closedBar}>
          ✓ SCORECARD CLOSED — Locked for betting line calculations
        </div>
      )}

      {/* Hole tabs */}
      <div style={s.holeNav}>
        {Array.from({ length: numHoles }, (_, i) => (
          <button key={i} onClick={() => setCurrentHole(i)}
            style={{ ...s.holeTab, ...(i === currentHole ? s.holeTabActive : {}), ...(myScores[i] != null ? s.holeTabDone : {}) }}>
            {i + 1}
          </button>
        ))}
      </div>

      <div style={s.body}>
        {/* Current hole */}
        <div style={s.holeCard}>
          <div style={s.holeHeader}>
            <div style={s.holeNum}>HOLE {currentHole + 1}</div>
            <div style={s.holePar}>Par {currentPar}</div>
          </div>

          <div style={s.scoreRow}>
            <button style={{ ...s.scoreBtn, opacity: !isLive ? 0.3 : 1 }} onClick={() => updateScore(-1)} disabled={!isLive}>−</button>
            <div style={s.scoreCenter}>
              <div style={s.scoreNum}>{currentScore ?? '—'}</div>
              {vsParKey !== null && (
                <div style={{ ...s.vsParLabel, color: VS_PAR_COLOR[vsParKey] || '#7A7570' }}>
                  {vsParNum > 0 ? '+' + vsParNum : vsParNum === 0 ? 'E' : vsParNum} · {VS_PAR_LABEL[vsParKey] || (vsParNum > 3 ? '+' + vsParNum : '')}
                </div>
              )}
            </div>
            <button style={{ ...s.scoreBtn, opacity: !isLive ? 0.3 : 1 }} onClick={() => updateScore(1)} disabled={!isLive}>+</button>
          </div>

          {isLive && (
            <div style={s.quickRow}>
              {[currentPar - 1, currentPar, currentPar + 1, currentPar + 2].map(v => (
                <button key={v} onClick={() => setHoleScore(v)}
                  style={{ ...s.quickBtn, ...(currentScore === v ? s.quickBtnActive : {}) }}>
                  {v}
                </button>
              ))}
            </div>
          )}

          {isLive && (
            <button style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }} onClick={saveAndAdvance} disabled={saving}>
              {saving ? 'Saving...' : isReview ? 'Save Correction →' : currentHole < numHoles - 1 ? 'Save & Next Hole →' : 'Save Last Hole'}
            </button>
          )}

          {isReview && (
            <div style={s.reviewNote}>
              You can still correct scores during the review window. Changes are visible to all players in your betting lines.
            </div>
          )}
        </div>

        {/* Other players */}
        {players.filter(pid => pid !== session.user.id).length > 0 && (
          <div style={s.othersCard}>
            <div style={s.othersTitle}>OTHER PLAYERS — HOLE {currentHole + 1}</div>
            {players.filter(pid => pid !== session.user.id).map(pid => {
              const sc = scores[pid]?.[currentHole]
              const vp = sc != null ? sc - (holePars[currentHole] || 4) : null
              return (
                <div key={pid} style={s.otherRow}>
                  <div style={s.otherName}>{profiles[pid]?.first_name} {profiles[pid]?.last_name}</div>
                  <div style={s.otherScore}>
                    {sc != null ? (
                      <>
                        <span style={s.otherRaw}>{sc}</span>
                        <span style={{ ...s.otherVsPar, color: vp < 0 ? '#00563F' : vp > 0 ? '#C0392B' : '#1A1814' }}>
                          {vp > 0 ? '+' + vp : vp === 0 ? 'E' : vp}
                        </span>
                      </>
                    ) : <span style={{ color: '#C0C0C0' }}>—</span>}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Totals */}
        <div style={s.totalsCard}>
          <div style={s.totalRow}>
            <div style={s.totalLabel}>HOLES PLAYED</div>
            <div style={s.totalVal}>{holesPlayed} / {numHoles}</div>
          </div>
          <div style={s.totalRow}>
            <div style={s.totalLabel}>TOTAL STROKES</div>
            <div style={s.totalVal}>{totalStrokes || '—'}</div>
          </div>
          <div style={s.totalRow}>
            <div style={s.totalLabel}>VS PAR</div>
            <div style={{ ...s.totalVal, color: totalVsPar < 0 ? '#00563F' : totalVsPar > 0 ? '#C0392B' : '#1A1814' }}>
              {holesPlayed > 0 ? (totalVsPar > 0 ? '+' + totalVsPar : totalVsPar === 0 ? 'E' : totalVsPar) : '—'}
            </div>
          </div>
        </div>

        {/* Submit button */}
        {isLive && holesPlayed === numHoles && (
          <button style={{ ...s.submitBtn, opacity: submitting ? 0.7 : 1 }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting...' : '✓ Submit Scorecard for Review'}
          </button>
        )}
      </div>
    </div>
  )
}

const s = {
  screen: { display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", background: '#FDF9E7' },
  toast: { position: 'fixed', top: 0, left: 0, right: 0, padding: '16px 24px', color: 'white', fontSize: 14, fontWeight: 600, textAlign: 'center', zIndex: 100 },
  reviewBar: { background: 'rgba(201,168,76,0.12)', borderBottom: '1px solid rgba(201,168,76,0.3)', padding: '10px 20px', fontSize: 12, fontWeight: 700, color: '#9B7D52', textAlign: 'center' },
  closedBar: { background: 'rgba(0,96,57,0.08)', borderBottom: '1px solid rgba(0,96,57,0.2)', padding: '10px 20px', fontSize: 12, fontWeight: 700, color: '#00563F', textAlign: 'center' },
  holeNav: { display: 'flex', overflowX: 'auto', padding: '12px 16px', gap: 6, background: '#FFFFFF', borderBottom: '1px solid #E0DDD4', flexShrink: 0 },
  holeTab: { minWidth: 34, height: 34, borderRadius: 10, border: '1.5px solid #E0DDD4', background: '#FDF9E7', fontSize: 13, fontWeight: 600, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
  holeTabActive: { background: '#00563F', border: '1.5px solid #00563F', color: 'white' },
  holeTabDone: { background: 'rgba(0,96,57,0.08)', border: '1.5px solid rgba(0,96,57,0.3)', color: '#00563F' },
  body: { padding: '16px 24px 48px', display: 'flex', flexDirection: 'column', gap: 12 },
  holeCard: { background: '#FFFFFF', borderRadius: 20, padding: 20, border: '1px solid #E0DDD4', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  holeHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  holeNum: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: '#1A1814', letterSpacing: 1 },
  holePar: { fontSize: 13, fontWeight: 700, color: '#7A7570', background: '#FDF9E7', borderRadius: 8, padding: '4px 12px' },
  scoreRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  scoreBtn: { width: 56, height: 56, borderRadius: 28, background: '#0A0A0A', border: 'none', color: 'white', fontSize: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' },
  scoreCenter: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  scoreNum: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 72, color: '#1A1814', lineHeight: 1 },
  vsParLabel: { fontSize: 14, fontWeight: 700, letterSpacing: 0.5 },
  quickRow: { display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 },
  quickBtn: { flex: 1, padding: '10px 0', background: '#FDF9E7', border: '1.5px solid #E0DDD4', borderRadius: 12, fontSize: 15, fontWeight: 700, color: '#1A1814', cursor: 'pointer', fontFamily: 'inherit' },
  quickBtnActive: { background: '#00563F', border: '1.5px solid #00563F', color: 'white' },
  saveBtn: { width: '100%', background: '#0A0A0A', color: 'white', border: 'none', borderRadius: 14, padding: '14px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 },
  reviewNote: { fontSize: 12, color: '#9B7D52', background: 'rgba(201,168,76,0.08)', borderRadius: 10, padding: '10px 12px', marginTop: 8, lineHeight: 1.5 },
  othersCard: { background: '#FFFFFF', borderRadius: 20, padding: 18, border: '1px solid #E0DDD4' },
  othersTitle: { fontSize: 10, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5, marginBottom: 12 },
  otherRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #EDE8D8' },
  otherName: { fontSize: 14, fontWeight: 600, color: '#1A1814' },
  otherScore: { display: 'flex', alignItems: 'center', gap: 8 },
  otherRaw: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: '#1A1814' },
  otherVsPar: { fontSize: 13, fontWeight: 700 },
  totalsCard: { background: '#0A0A0A', borderRadius: 20, padding: 18, display: 'flex', flexDirection: 'column', gap: 10 },
  totalRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  totalLabel: { fontSize: 11, fontWeight: 700, color: '#444', letterSpacing: 1.5, textTransform: 'uppercase' },
  totalVal: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: '#FDF9E7' },
  submitBtn: { background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' },
}
